import { useState, useEffect, useRef, useCallback } from "react";
import Main from "../../components/layouts/Main";
import PageTitle from "../../components/utils/PageTitle";
import { useUserContext } from "../../contexts/UserContext";
import Breadcrumb from "../../components/utils/Breadcrumb";
import { catchError } from "../../services/DaoService";
import { Button, Card, Col, Collapse, Select, Spin, Empty } from "antd";
import { FolderOpenOutlined } from "@ant-design/icons";
import { PlusCircleFilled, ReloadOutlined } from "@ant-design/icons";
import DetailPreferenceToggle from "../../components/utils/DetailPreferenceToggle";
import AffaireCreateForm from "../../components/affaire/AffaireCreateForm";
import AffaireBacklogItem from "../../components/affaire/AffaireBacklogItem";
import AffaireDoneSection from "../../components/affaire/AffaireDoneSection";
import AffaireQuickView from "../../components/affaire/AffaireQuickView";
import AdvancedFilterPanel from "../../components/utils/AdvancedFilterPanel";
import { ModeleService } from "../../services/ModeleService";
import { PrioriteService } from "../../services/PrioriteService";
import { EtapeService } from "../../services/EtapeService";
import { DepartementService } from "../../services/DepartementService";
import { AffaireService } from "../../services/AffaireService";
import { useSelector } from "@legendapp/state/react";
import { state$ } from "../../state/globalState";
import { affairesState } from "../../state/actions/affairesState";
import { useDetailPreference } from "../../hooks/useDetailPreference";

export default function AffaireListPage() {
  const [newModalVisible, setNewModalVisible] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const { check } = useUserContext();
  const { mode, changeMode } = useDetailPreference();
  const loading = useSelector(() => state$.affaires.loading.get());
  const affaires = useSelector(() => state$.affaires.list.get());
  const filters = useSelector(() => state$.affaires.filters.get());
  const pagination = useSelector(() => state$.affaires.pagination.get());
  const sentinelRef = useRef(null);

  const [modeles, setModeles] = useState([]);
  const [etapes, setEtapes] = useState([]);
  const [priorites, setPriorites] = useState([]);
  const [departements, setDepartements] = useState([]);

  useEffect(() => {
    Promise.all([
      ModeleService.getInstance().all(),
      EtapeService.getInstance().all(),
      PrioriteService.getInstance().all(),
      DepartementService.getInstance().all(),
    ])
      .then(([m, e, p, d]) => {
        setModeles(m.data);
        setEtapes(e.data);
        setPriorites(p.data);
        setDepartements(d.data);
      })
      .catch(catchError);
  }, []);

  useEffect(() => {
    affairesState.refreshAffaires();
  }, [
    filters.searchText, filters.statut, filters.responsable_id,
    filters.modele_id, filters.etape_id, filters.priorite_id,
    filters.departement_id, filters.date_from, filters.date_to,
    filters.created_from, filters.created_to, filters.my_role,
  ]);

  const handleIntersect = useCallback((entries) => {
    if (entries[0].isIntersecting && pagination?.next_page_url && !loading) {
      affairesState.loadMoreAffaires();
    }
  }, [pagination, loading]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersect, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const setFilter = (key, value) => {
    state$.affaires.filters.set({ ...state$.affaires.filters.get(), [key]: value });
  };

  const getResponsables = (currentFilters) =>
    AffaireService.getInstance().getResponsables(currentFilters);

  const extraFilters = (
    <>
      <Col xs={24} sm={12} lg={8}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8C9BAB' }}>Type de dossier</div>
          <Select value={filters.modele_id} onChange={v => setFilter('modele_id', v)} allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Type"
            options={modeles.map(m => ({ value: m.id, label: m.nom }))} />
        </div>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8C9BAB' }}>Étape</div>
          <Select value={filters.etape_id} onChange={v => setFilter('etape_id', v)} allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Étape"
            options={etapes.map(e => ({ value: e.id, label: e.nom }))} />
        </div>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8C9BAB' }}>Priorité</div>
          <Select value={filters.priorite_id} onChange={v => setFilter('priorite_id', v)} allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Priorité"
            options={priorites.map(p => ({ value: p.id, label: p.nom }))} />
        </div>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8C9BAB' }}>Département</div>
          <Select value={filters.departement_id} onChange={v => setFilter('departement_id', v)} allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Département"
            options={departements.map(d => ({ value: d.id, label: `${d.nom} (${d.code})` }))} />
        </div>
      </Col>
    </>
  );

  return (
    <Main>
      <Breadcrumb parentLink="/" canSee={check("VIEW-DASHBOARD")} parentLinkTitle="Accueil" title="Dossiers" />
      <PageTitle title="Dossiers" subTitle={`${affaires.length} dossier${affaires.length !== 1 ? 's' : ''}${pagination?.total ? ` sur ${pagination.total}` : ''}`}>
        {check("CREATE-AFFAIRE") && (
          <Button type="primary" icon={<PlusCircleFilled />} onClick={() => setNewModalVisible(true)}>
            Nouveau dossier
          </Button>
        )}
        <Button icon={<ReloadOutlined />} onClick={() => affairesState.refreshAffaires()}>
          Rafraîchir
        </Button>
      </PageTitle>

      <DetailPreferenceToggle mode={mode} onChange={changeMode} />

      <Card styles={{ body: { padding: 0 } }}>
        <AdvancedFilterPanel
          filters={filters}
          onFilterChange={setFilter}
          getResponsables={getResponsables}
          extraFilters={extraFilters}
          searchPlaceholder="Rechercher un dossier (nom, code)..."
          searchValue={filters.searchText || ''}
          onSearch={v => setFilter('searchText', v || null)}
          showRoleFilter={true}
          roleFilterValue={filters.my_role}
          onRoleFilterChange={v => setFilter('my_role', v)}
        />
        <Collapse
          defaultActiveKey={['active']}
          ghost={false}
          style={{ borderRadius: 0 }}
          items={[{
            key: 'active',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, fontSize: 13 }}>
                <FolderOpenOutlined style={{ color: '#3E65AC' }} />
                Ouverts
                {pagination?.total != null && (
                  <span style={{ fontSize: 12, color: '#8C9BAB', fontWeight: 400 }}>({pagination.total})</span>
                )}
              </span>
            ),
            children: (
              <Spin spinning={loading && affaires.length === 0}>
                {affaires.length === 0 && !loading
                  ? <div style={{ padding: '24px 0' }}><Empty description="Aucun dossier ne correspond aux filtres" /></div>
                  : (
                    <div>
                      {affaires.map(affaire => (
                        <div key={affaire.uid} style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}>
                          <AffaireBacklogItem affaire={affaire} onItemClick={setSelectedUid} />
                        </div>
                      ))}
                      <div ref={sentinelRef} style={{ height: 1 }} />
                      {loading && affaires.length > 0 && (
                        <div style={{ textAlign: 'center', padding: '12px 0' }}>
                          <Spin size="small" />
                        </div>
                      )}
                    </div>
                  )
                }
              </Spin>
            ),
          }]}
        />
        <AffaireDoneSection filters={filters} onItemClick={setSelectedUid} />
      </Card>

      {newModalVisible && (
        <AffaireCreateForm
          visible={newModalVisible}
          onCreate={() => { affairesState.refreshAffaires(); setNewModalVisible(false); }}
          onCancel={() => setNewModalVisible(false)}
        />
      )}

      <AffaireQuickView
        uid={selectedUid}
        mode={mode}
        onClose={() => {
          // Si le dossier consulté a été clôturé pendant qu'il était ouvert, on ne
          // le retire du backlog qu'à la fermeture du panneau — le retirer dès le
          // onUpdate ferait disparaître le contenu du panneau lui-même, puisqu'il
          // est affiché à partir de cette même liste (state$.affaires.list).
          const closedUid = selectedUid;
          setSelectedUid(null);
          const closedAffaire = state$.affaires.list.get().find(a => a.uid === closedUid);
          if (closedAffaire?.done) {
            affairesState.removeAffaireFromList(closedUid);
          }
        }}
      />
    </Main>
  );
}
